import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import { useNavigate, Link } from '@tanstack/react-router';
import client from '@/api/client';
import type { UserRole } from '@/api/types';

const HOME: Record<UserRole, string> = {
  ADMIN: '/admin/dashboard',
  HOSTEL_OWNER: '/owner/dashboard',
  SUPERVISOR: '/supervisor/dashboard',
  TENANT: '/tenant/dashboard',
};

export function LoginPage() {
  const { t } = useTranslation();
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [loading, setLoading]   = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendEmailInput, setResendEmailInput] = useState('');

  const [showForgot, setShowForgot]       = useState(false);
  const [resetMethod, setResetMethod]     = useState<'EMAIL_LINK' | 'SMS_OTP'>('EMAIL_LINK');
  const [forgotEmail, setForgotEmail]     = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError]     = useState('');
  const [forgotSent, setForgotSent]       = useState(false);

  const [otp, setOtp]                 = useState('');
  const [otpPassword, setOtpPassword] = useState('');
  const [otpConfirm, setOtpConfirm]   = useState('');
  const [otpLoading, setOtpLoading]   = useState(false);
  const [otpError, setOtpError]       = useState('');
  const [otpSuccess, setOtpSuccess]   = useState(false);

  if (user) {
    void navigate({ to: HOME[user.role] });
    return null;
  }

  const handleResendVerification = async () => {
    try {
      await client.post('/api/auth/resend-verification', { identifier });
      setResendSent(true);
    } catch {
      setResendSent(true);
    }
  };

  const handleResendFromPanel = async () => {
    const target = resendEmailInput.trim() || identifier.trim();
    if (!target) return;
    try {
      await client.post('/api/auth/resend-verification', { identifier: target });
    } catch { /* backend always responds the same */ }
    setResendSent(true);
    setShowResend(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrorCode('');
    setResendSent(false);
    setLoading(true);
    try {
      await login(identifier, password);
      const raw = localStorage.getItem('authUser');
      const u = raw ? (JSON.parse(raw) as { role: UserRole }) : null;
      void navigate({ to: u ? HOME[u.role] : '/' });
    } catch (err) {
      const code = (err as Error & { code?: string }).code ?? '';
      setErrorCode(code);
      setError(err instanceof Error ? err.message : t('auth.loginFailed', 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      await client.post('/api/auth/forgot-password', { identifier: forgotEmail, method: resetMethod });
      setForgotSent(true);
    } catch (err) {
      setForgotError(err instanceof Error ? err.message : t('auth.requestFailed', 'Request failed'));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    if (otpPassword !== otpConfirm) {
      setOtpError(t('auth.passwordsDoNotMatch', 'Passwords do not match'));
      return;
    }
    if (otpPassword.length < 8) {
      setOtpError(t('auth.passwordMinLength', 'Password must be at least 8 characters'));
      return;
    }
    setOtpLoading(true);
    try {
      await client.post('/api/auth/reset-password', { token: otp, newPassword: otpPassword });
      setOtpSuccess(true);
    } catch (err) {
      setOtpError(err instanceof Error ? err.message : t('auth.resetFailed', 'Reset failed. The code may be invalid or expired.'));
    } finally {
      setOtpLoading(false);
    }
  };

  const backToSignIn = () => {
    setShowForgot(false);
    setForgotSent(false);
    setForgotError('');
    setOtp('');
    setOtpPassword('');
    setOtpConfirm('');
    setOtpError('');
    setOtpSuccess(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('auth.appTitle', 'Hostel Management')}</h1>
          <p className="text-gray-500 mt-2">{t('auth.loginTitle')}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          {!showForgot ? (
            <>
              <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.identifier', 'Email, phone or username')}</label>
                  <input
                    type="text"
                    value={identifier}
                    onChange={e => setIdentifier(e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('auth.identifierPlaceholder', 'you@example.com or phone number')}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">{t('auth.password')}</label>
                    <button
                      type="button"
                      onClick={() => { setShowForgot(true); setForgotEmail(identifier); }}
                      className="text-xs text-blue-600 hover:underline font-medium"
                    >
                      {t('auth.forgotPassword')}
                    </button>
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                </div>
                {error && (
                  <div>
                    <p className="text-red-600 text-sm">{error}</p>
                    {errorCode === 'EMAIL_NOT_VERIFIED' && (
                      <div className="mt-2">
                        {resendSent ? (
                          <p className="text-green-600 text-xs">Verification email sent — check your inbox.</p>
                        ) : (
                          <button
                            type="button"
                            onClick={() => void handleResendVerification()}
                            className="text-blue-600 text-xs underline hover:text-blue-800"
                          >
                            Resend verification email
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-50"
                >
                  {loading ? t('auth.signingIn', 'Signing in…') : t('auth.signIn')}
                </button>
              </form>
              <p className="text-center text-sm text-gray-500 mt-6">
                {t('auth.newOwnerPrompt', 'New hostel owner?')}{' '}
                <Link to="/register" className="text-blue-600 font-medium hover:underline">
                  {t('auth.registerHere', 'Register here')}
                </Link>
              </p>
              <p className="text-center text-sm text-gray-500 mt-2">
                {t('auth.tenantQuestion', 'Tenant?')}{' '}
                <Link to="/tenant/signup" className="text-blue-600 font-medium hover:underline">
                  {t('auth.tenantSignup')}
                </Link>
              </p>

              <div className="mt-5 border-t border-gray-100 pt-4">
                {resendSent ? (
                  <p className="text-center text-xs text-green-600">Verification email sent — check your inbox.</p>
                ) : showResend ? (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 text-center">Enter your email to get a new verification link</p>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={resendEmailInput || identifier}
                      onChange={e => setResendEmailInput(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => void handleResendFromPanel()}
                        className="flex-1 bg-blue-600 text-white rounded-lg py-2 text-sm font-medium hover:bg-blue-700 transition"
                      >
                        Send verification email
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowResend(false)}
                        className="px-3 border rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-xs text-gray-400">
                    Didn&apos;t receive a verification email?{' '}
                    <button
                      type="button"
                      onClick={() => { setShowResend(true); setResendEmailInput(identifier); }}
                      className="text-blue-600 font-medium hover:underline"
                    >
                      Resend it
                    </button>
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <button
                onClick={backToSignIn}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-5"
              >
                ← {t('auth.backToSignIn', 'Back to sign in')}
              </button>

              <h2 className="text-xl font-semibold text-gray-900 mb-1">{t('auth.resetPassword')}</h2>

              {/* Step 1: not yet requested — choose method + enter email */}
              {!forgotSent && (
                <>
                  <p className="text-sm text-gray-500 mb-5">
                    {t('auth.enterRegisteredEmail', 'Enter your registered email address and choose how you want to verify it’s you.')}
                  </p>
                  <div className="flex gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => setResetMethod('EMAIL_LINK')}
                      className={`flex-1 text-sm font-medium py-2 rounded-lg border transition ${resetMethod === 'EMAIL_LINK' ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                    >
                      {t('auth.methodEmailLink', 'Email me a link')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setResetMethod('SMS_OTP')}
                      className={`flex-1 text-sm font-medium py-2 rounded-lg border transition ${resetMethod === 'SMS_OTP' ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                    >
                      {t('auth.methodSmsOtp', 'Text me a code')}
                    </button>
                  </div>
                  <form onSubmit={(e) => { void handleForgot(e); }} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.email')}</label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)}
                        required
                        autoFocus
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="you@example.com"
                      />
                    </div>
                    {forgotError && <p className="text-red-600 text-sm">{forgotError}</p>}
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-50"
                    >
                      {forgotLoading
                        ? t('auth.submitting', 'Submitting…')
                        : resetMethod === 'EMAIL_LINK'
                          ? t('auth.sendResetLink', 'Send Reset Link')
                          : t('auth.sendResetCode', 'Send Code')}
                    </button>
                  </form>
                </>
              )}

              {/* Step 2a: email link requested — done, self-service email is on its way */}
              {forgotSent && resetMethod === 'EMAIL_LINK' && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                  <p className="font-medium mb-1">{t('auth.checkYourEmail', 'Check your email')}</p>
                  <p>
                    {t('auth.resetLinkSentPrefix', 'If an account exists for')}{' '}
                    <strong>{forgotEmail}</strong>, {t('auth.resetLinkSentSuffix', 'we’ve sent a password reset link. It expires in 24 hours.')}
                  </p>
                </div>
              )}

              {/* Step 2b: SMS OTP requested — enter code + new password */}
              {forgotSent && resetMethod === 'SMS_OTP' && !otpSuccess && (
                <>
                  <p className="text-sm text-gray-500 mb-5">
                    {t('auth.otpSentHint', 'If an account exists for this email with a phone number on file, we’ve texted a 6-digit code. It expires in 10 minutes.')}
                  </p>
                  <form onSubmit={(e) => { void handleVerifyOtp(e); }} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.otpCode', 'Verification code')}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={otp}
                        onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                        required
                        autoFocus
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="123456"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.newPassword')}</label>
                      <input
                        type="password"
                        value={otpPassword}
                        onChange={e => setOtpPassword(e.target.value)}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Minimum 8 characters"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('auth.confirmPassword')}</label>
                      <input
                        type="password"
                        value={otpConfirm}
                        onChange={e => setOtpConfirm(e.target.value)}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Re-enter new password"
                      />
                    </div>
                    {otpError && <p className="text-red-600 text-sm">{otpError}</p>}
                    <button
                      type="submit"
                      disabled={otpLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-50"
                    >
                      {otpLoading ? t('auth.updating', 'Updating…') : t('auth.setNewPassword', 'Set New Password')}
                    </button>
                  </form>
                </>
              )}

              {/* Step 3: OTP flow succeeded */}
              {forgotSent && resetMethod === 'SMS_OTP' && otpSuccess && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                  <p className="font-medium mb-1">{t('auth.passwordUpdated', 'Password updated')}</p>
                  <p className="mb-3">{t('auth.passwordResetSuccess', 'Your password has been reset successfully.')}</p>
                  <button onClick={backToSignIn} className="text-green-800 font-medium hover:underline">
                    {t('auth.backToSignIn', 'Back to sign in')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
